export interface DefenseCardItem {
  icon: 'globe' | 'database' | 'layers' | 'fingerprint'
  tag: string
  title: string
  desc: string
  footnote?: string
  link?: { label: string; url: string }
}

export interface SecuritySection {
  id: string
  title: string
  nav: string
  lead: string
  paragraphs: string[]
  evidence: string[]
  note?: string
}

const zh = {
  nav: '安全性报告',
  mobileNav: '安全',
  close: '关闭安全说明',
  author: '小淳',
  readReport: '查看完整技术报告',
  acknowledge: '我知道了',
  notice: {
    kicker: '透明度公告',
    title: '关于账号安全与封号疑问的澄清',
    officialResponse: '已收录冒险岛官方回应',
    officialContext: '由冒险岛官方主播协助咨询，完整聊天记录可在报告中查阅。',
    intro: '近期有玩家关心使用枫记是否会造成盗号或封号。作为个人独立作者，与其说空洞的口头保证，不如把枫记的权限边界、画面处理机制与真实代码公开说明。',
    detail: '完整报告附有真实源码与 F12 核查指引，可自行验证技术实现及数据流向。',
  },
  kicker: '技术实现、权限边界与架构安全透明公开',
  title: '枫记安全与隐私报告',
  intro: '关于盗号与封号的传言，我非常理解所有玩家的顾虑。枫记是由我个人独立制作的纯前端小工具，与其反复做空洞的“请放心”保证，不如把网页的技术底层、数据流向和代码细节全部公开。枫记能做到什么、做不到什么，均由浏览器沙箱与实际代码决定，任何人都可以对照核验。',
  updated: '更新于',
  version: '适用构建',
  scope: '本报告由作者基于当前线上构建提供技术说明，代码真实透明，欢迎任何人监督核查。',
  summaryTitle: '底牌声明：枫记做不到什么',
  facts: [
    { label: '账号凭据', title: '不碰游戏账号密码', detail: '纯前端离线运行，无任何登录框，绝不接触、读取或保存任何游戏密码与身份凭据。' },
    { label: '图像处理', title: '共享画面在本机闭环识别', detail: '画面与 OCR 原文 100% 留在本地内存处理，不传云端，不保存录像。' },
    { label: '客户端交互', title: '不修改客户端，不模拟游戏操作', detail: '不读写游戏内存、不注入进程、不替玩家按键，不存在破坏性交互。' },
  ],
  contents: '报告目录',
  currentSection: '当前阅读',
  workflowTitle: '系统架构与数据流向',
  workflowLead: '画面识别与效率统计全程在您的本地浏览器闭环运行，完全受浏览器沙箱安全隔离。',
  workflow: [
    { title: '窗口授权', detail: '系统弹出选择器，由您主动勾选共享的游戏窗口' },
    { title: '视频帧捕获', detail: '仅接收选定区域的画面，音频固定禁用' },
    { title: '本地离线识别', detail: 'Web Worker 内运行 WASM 模型提取数字' },
    { title: '本地数据库存储', detail: '效率计算与历史明细保存在本机 IndexedDB' },
  ],
  localBoundary: '本地执行环境：完全受限在当前浏览器的沙箱内',
  uploadBranch: '可选排行同步：单图满 30 分钟的统计摘要 ➔ 枫记接口 ➔ 小册子排行榜',
  uploadBranchNote: '仅同步公开统计数字（地图名、经验值等），不含游戏画面，与账号安全主线无关。',
  sections: [
    {
      id: 'account',
      nav: '账号凭据',
      title: '账号凭据与隔离边界',
      lead: '枫记只是基于画面像素的统计计算器，完全独立于游戏账号体系。',
      paragraphs: [
        '网站没有任何游戏账号的登录界面，绝不索取你的游戏账号、密码、密保或二次验证码。输入源仅为你主动授权共享的游戏画面。',
        '> **原理解析：为什么网页绝不可能偷录键盘与鼠标？**\n> 1. **焦点隔离**：网页代码只能接收自身获得焦点时的按键事件。当您在游戏内输入密码时，按键由操作系统直接发给游戏进程，后台网页无从感知。\n> 2. **沙箱阻断全局钩子**：窃听跨软件按键必须借助底层系统钩子（如 Windows 的 SetWindowsHookEx）或驱动。现代浏览器沙箱在操作系统层面对网页代码完全封锁了此能力，只有可执行文件（.exe）或木马才可能做到。\n> 3. **鼠标轨迹隔离**：一旦光标移出浏览器，网页立刻失去对鼠标的一切追踪，无法记录任何游戏内操作。',
        '浏览器提供的 getDisplayMedia 接口只输出视频流画面，从底层机制上根本不赋予它读写其他进程内存、提取登录凭据或嗅探其他网站 Cookie 的任何权限。下方展示采集入口的真实代码，任何人均可核对。',
      ],
      evidence: ['capture-request'],
    },
    {
      id: 'capture',
      nav: '捕获权限',
      title: '屏幕捕获范围与权限控制',
      lead: '网页只能看到你授权的那一块窗口，音频采集已被底层代码写死禁用。',
      paragraphs: [
        '发起屏幕采集时，必须由操作系统的窗口选择器由你亲自选择。网页技术无法绕过用户弹窗私自偷录屏幕，且代码中已显式配置禁用音频捕获。',
        '需要提醒的是：界面上的校准裁剪仅用于缩小后续文字识别的范围，而非改变系统授予的捕获源。若选择共享整个桌面，网页依然能读取桌面上露出的像素，因此强烈建议始终只共享游戏窗口。',
        '你可以随时点击浏览器顶部的“停止共享”，或在站内断开连接，媒体轨道将被彻底调用 stop() 销毁释放。',
      ],
      evidence: ['capture-options', 'capture-stop'],
    },
    {
      id: 'local',
      nav: '识别与存储',
      title: '本地离线识别与数据存储',
      lead: 'PaddleOCR 模型通过 WASM 在本地浏览器多线程运行，数据留在本机。',
      paragraphs: [
        '> **关于 PaddleOCR**：由百度飞桨团队开源的端到端超轻量文字识别系统。',
        '截取的画面帧在本地 Web Worker 内由 WebAssembly（WASM）推理引擎计算，纯粹使用你本机的 CPU 资源提取经验与等级数字。该过程不调用任何云端识别接口，原始画面和文本决不上传。',
        '识别模型等静态资源在首次使用时从公共 CDN 下载并缓存在本地浏览器，资源均经过 SHA-256 完整性核对，杜绝第三方篡改。',
        '你的练习记录、校准参数和本地配置统一存放在当前浏览器的 IndexedDB 数据库（名为 maple-log）中，完全属于你本地掌控。',
      ],
      evidence: ['ocr-worker', 'ocr-predict', 'local-save', 'resource-integrity'],
    },
    {
      id: 'game',
      nav: '客户端交互',
      title: '游戏客户端零交互机制',
      lead: '枫记是纯网页工具，不注入、不改内存、不向游戏发送任何按键指令。',
      paragraphs: [
        '很多玩家担心封号。从技术原理来看：枫记无需安装客户端、不挂载驱动、不注入 DLL、不读写游戏进程内存，更不包含任何模拟鼠标键盘的按键脚本。在系统和反作弊眼里，它只是一个在看画面的普通网页。',
        '所有“开始”、“暂停”按钮仅控制网页内部的计时器与识别流水线，完全没有与游戏程序进行指令交互的能力。',
        '以上为当前代码的技术行为。游戏运营方对第三方工具的规则适用性拥有判定权，已补充的沟通记录见第 01 节。',
      ],
      evidence: ['ocr-predict'],
    },
    {
      id: 'defense',
      nav: '防入侵机制',
      title: '架构防御与“被入侵”辟谣：为什么不存在截取数据的可能',
      lead: '即使极端假设网站服务器遭受网络攻击，也绝不可能截取或窃取您的游戏数据。',
      paragraphs: [
        '很多玩家难免会产生疑虑：“网站如果被黑客攻破，会不会截取我的游戏数据？网址本身是不是冒牌或钓鱼页面？”作为个人独立开发者，从系统架构设计之初就通过“纯静态前端 + 零中心存储 + 双域名正规备案”从底层彻底切断了此类风险路径。',
        '黑客攻击只能窃取服务器上已有的数据，而枫记的服务端不保存玩家的任何游戏画面或记录。以下四道核心安全防线，从官方域名权威备案、数据源头隔离、浏览器沙箱限制到构建完整性校验，构成了完整的安全闭环：',
      ],
      evidence: [],
    },
    {
      id: 'data',
      nav: '网络数据',
      title: '网络数据分类：唯一离开电脑的数据项',
      lead: '游戏画面与原始截屏绝不上报；仅达标的公开统计摘要会进入排行榜。',
      paragraphs: [
        '为方便玩家查看各职业在不同地图的升级效率，当单张地图在同一等级段内有效时长累计达到 30 分钟、且经验增量为正时，该段经验的统计摘要会自动上传并同步至小册子（mxdc.dvg.cn）公开排行榜，供大家参考练级效率。',
        '这项功能纯粹为了社区经验交流，同步请求由严格的字段白名单过滤构造，绝不打包您的本地历史数据库，更不会上报游戏画面。下表完整披露了数据处理分类：',
      ],
      evidence: ['rank-fields', 'rank-submit', 'visitor-id'],
    },
    {
      id: 'verify',
      nav: '证据复核',
      title: '三步验证：打开 F12 亲自核查',
      lead: '不用盲目相信承诺。任何人都可以借助浏览器开发者工具（F12）核对每一条事实。',
      paragraphs: [
        '> **原理解析：为什么网页代码天然公开？**\n> 安装包、exe 编译之后，普通人很难直接读里面的逻辑。网页正好相反：浏览器要运行枫记，就必须把当前这份 HTML、CSS、JavaScript 完整下载到你的电脑里再执行。\n> 所以你正在使用的代码，本来就在本机，按 F12 就能打开查看。',
        '本页展示的代码片段直接提取自线上运行的构建产物，带有文件路径、真实行号与 SHA-256 哈希。虽然浏览器安全策略禁止网页直接操控你的 DevTools，但你可以按照以下指引核查：',
        '真正的安全经得起检验。结合代码、网络抓包与本地数据库，便能完整验证整个链路的一致性。',
      ],
      evidence: [],
    },
  ] satisfies SecuritySection[],
  defenseCards: [
    {
      icon: 'globe' as const,
      tag: '官方正版 · 权威备案',
      title: '正版双域名与“钓鱼网站”辟谣',
      desc: '目前枫记官方正版网址仅有两处（fj.need.run 与 log.maplestory.pro），两者完全同源同库、功能一致。网络上传言“fj.need.run 是钓鱼网站”纯属毫无事实依据的谣言，两个网址均已依法完成工信部网站 ICP 正规备案并受国家严格监管。',
      link: { label: '前往国家工信部备案系统查询', url: 'https://beian.miit.gov.cn/' },
    },
    {
      icon: 'database' as const,
      tag: '无状态前端',
      title: '源头零敏感数据：服务器无物可偷',
      desc: '黑客入侵的前提是有高价值的敏感数据可供窃取。枫记为纯静态无状态前端架构，既不设账号密码登录窗口，更无任何中心化玩家数据库。所有的截屏画面与经验统计明细仅留存在您的本机内存与 IndexedDB 中，服务器上空无一物，根本无从窃取。',
      footnote: '防护底线：服务器零存储，黑客入侵亦无敏感数据可盗',
    },
    {
      icon: 'layers' as const,
      tag: '内核级物理隔离',
      title: '浏览器沙箱限制：无法越权触碰游戏',
      desc: '网页代码受现代操作系统与浏览器沙箱物理限制，从底层彻底封锁了读取本地文件、监听全局键盘按键、注入游戏进程或提取其他软件内存的全部路径。即便发生极端不可控的前端注入，恶意代码也只能被牢牢锁在浏览器单标签页沙箱内，绝无危害游戏的物理通道。',
      footnote: '物理边界：受限于浏览器沙箱内核，绝无注入游戏进程之可能',
    },
    {
      icon: 'fingerprint' as const,
      tag: '构建级防篡改',
      title: '静态指纹防篡改：中间人劫持即刻阻断',
      desc: '线上部署的所有前端 JavaScript 代码、Web Worker 脚本与离线 PaddleOCR 权重文件在发布时均计算并锁定了不可篡改的 SHA-256 密码学指纹。网络链路中的任何中间人劫持或非法注入，都会直接触发浏览器原生完整性校验报错并立刻阻断运行。',
      footnote: '防篡改链：代码模型均受 SHA-256 校验，劫持即报错阻断',
    },
  ],
  dataHeaders: ['数据类别', '具体内容与处理方式', '去向与安全边界'],
  dataRows: [
    ['共享画面与截屏', '在内存中做局部数字识别，不保存录像，不传云端', '本机内存处理，绝不上报'],
    ['本地历史与配置', '升级历程、收益统计、校准区域与用户偏好', '本机 IndexedDB 存储'],
    ['公开排行统计摘要', '单张地图满 30 分钟且经验为正的地图名、等级段、职业、时长与经验增量', '自动同步至小册子排行榜'],
    ['匿名辅助标识', '随机浏览器标识（用于排行榜去重，非游戏账号）', '随排行统计摘要提交'],
    ['静态模型与查询', 'OCR 权重文件、界面图标下载，以及排行榜公开数据查询', '本站及公共 CDN 资源服务'],
  ],
  verifySteps: [
    {
      title: 'Sources · 核对源码',
      text: '展开下方的代码证据，点击复制定位信息。按 F12 打开开发者工具 → 切换到 Sources → 按 Ctrl+P 搜索文件名并跳转到标注行号。因开启了 Source Maps，可直接阅读 TypeScript 原始代码。',
    },
    {
      title: 'Network · 监控抓包',
      text: '在 Network 面板勾选 Preserve log，正常使用并结束一次记录。查看提交至 /api/v1/rank/submissions 的 POST 请求体，可以亲眼确认只有纯 JSON 数字字段，绝无图片上传。',
    },
    {
      title: 'Application · 检查本地存储',
      text: '在 Application → IndexedDB → maple-log 中，可自由浏览 sessions、profiles 与 settings。所有明细与个人数据完全保存在你自己的电脑里。',
    },
  ],
  officialResponse: {
    title: '官方对枫记是否会封号的态度',
    nav: '官方态度',
    intro: '我委托冒险岛官方主播「无限跳票中」向游戏官方检测组咨询枫记的使用问题，相关沟通记录附后。',
    conclusion: '此次回复未表示禁止使用枫记，也未表示使用枫记就会封号。',
    records: {
      detection: {
        label: '检测组回复',
        translation: 'Literal translation: “Probably not, I think.”',
        recordedAt: '记录时间：8月29日 21:09（原图未标注年份）',
        imageAlt: '检测组沟通记录：询问这种网页版的经验统计能用么，对方回复应该不会吧。',
        imageCaption: '检测组沟通记录 · 回复节选',
      },
      relay: {
        label: '官方主播转达',
        translation: 'Literal translation: “The MapleStory detection team said it should be fine.”',
        recordedAt: '',
        imageAlt: '官方主播转达：冒险岛检测那边说应该没事。',
        imageCaption: '官方主播转达记录',
      },
    },
    privacyLabel: '隐私说明：',
    privacyNote: '为保护隐私，未展示对方头像。我保证以上沟通记录真实有效。',
    streamerLabel: '冒险岛官方主播 · 斗鱼',
    roomLabel: '房间号',
    sourceNote: '主播身份及联系经过由作者提供；直播间信息核对于 2026年9月20日。',
    enlarge: '放大查看聊天记录',
    enlargeAction: '放大查看',
    viewFullRecord: '查看完整记录',
    openOriginal: '查看原图',
    previewTitle: '聊天记录原图',
    imageError: '图片暂时无法加载，可通过“查看原图”链接打开。',
    scopeLabel: '答复范围：',
    scope: '适用于本次咨询的网页版经验统计方式，不涉及其他工具、插件或使用行为。',
    copyLink: '复制链接 · 官方对枫记是否会封号的态度',
  },
  footerTitle: '透明报告引用与反馈',
  footerText: '欢迎转载或引用本报告，请保留报告链接以便其他玩家查阅最新证据。若对代码实现有任何疑问或发现事实出入，欢迎随时联系我核实。',
  legalAttestation: '本报告中的技术说明、源码证据及相关资料均真实有效。我本人对上述内容承担法律责任。',
  contact: '联系作者',
  share: '分享报告',
  copyLink: '复制链接',
  copySummary: '复制核心摘要',
  shareImage: '保存分享图',
  shareImageSaving: '正在生成分享图…',
  shareImageSaved: '分享图已保存',
  shareImageFailed: '分享图生成失败，请稍后重试',
  shareImageFile: '枫记-安全性报告.png',
  shareCardKicker: '枫记不会盗号 · 本地识别 · 可自行核验',
  shareCardLead: '纯网页工具，不读内存、不注入、不模拟按键。完整证据见报告页。',
  shareCardDomains: '官方正版网址（均已备案）',
  shareCardCta: '完整报告与源码证据',
  nativeShare: '系统分享',
  print: '打印 / 另存为 PDF',
  shareSummary: '【枫记安全与隐私报告】不碰账号密码，共享画面在本地离线识别，不读写内存、不注入游戏或模拟按键。达标经验摘要会自动上传参与公开排行。完整源码证据与自查指引：',
  copied: '已复制到剪贴板',
  copyFailed: '复制失败，请手动长按复制下方内容',
  shareFailed: '无法调用系统分享，已为您备好复制链接',
  manualCopy: '可手动复制的内容',
  evidence: '查看源码证据',
  source: '源码位置',
  codeCopy: '复制代码片段',
  openResource: '在新标签页打开构建文件',
  locate: '复制 F12 定位指引',
  loadingEvidence: '正在调取本次构建的代码证据…',
  evidenceError: '暂时无法读取代码证据。正文仍可正常阅读，请重试。',
  retry: '重新加载代码证据',
  evidenceScope: '代码提取自当前生产构建产物；SHA-256 摘要供完整性核验。',
  digest: '证据指纹 SHA-256',
  evidenceLabels: {
    'capture-request': '浏览器画面授权入口',
    'capture-options': '捕获范围选项与音频关闭',
    'capture-stop': '停止并释放共享轨道',
    'ocr-worker': 'Worker 与本机 WASM 运行配置',
    'ocr-predict': '本地 OCR 推理调用',
    'local-save': '历史记录的本地持久化',
    'resource-integrity': '下载内容的 SHA-256 校验',
    'rank-fields': '提交字段的显式白名单',
    'rank-submit': '实际提交接口与请求体',
    'visitor-id': '随机浏览器标识的生成方式',
  } as Record<string, string>,
}

const en: typeof zh = {
  nav: 'Safety report',
  mobileNav: 'Safety',
  close: 'Close notice',
  author: 'Xiaochun',
  readReport: 'View full technical report',
  acknowledge: 'Understood',
  notice: {
    kicker: 'Transparency notice',
    title: 'A clarification on account safety and ban concerns',
    officialResponse: 'MapleStory’s official response included',
    officialContext: 'An official MapleStory streamer relayed the inquiry. See the report for the full conversation.',
    intro: 'Some players have asked whether using Maple Log could compromise account security or lead to bans. As an independent solo developer, rather than making vague verbal promises, here is full transparency on technical boundaries, data flows, and code.',
    detail: 'The report includes source code and F12 verification steps so you can check the implementation and data flow yourself.',
  },
  kicker: 'Technical implementation, permissions, and architectural security transparency',
  title: 'Maple Log safety & privacy report',
  intro: 'I understand all player concerns regarding account theft and bans. Maple Log is a personal independent project. Instead of repeating empty "don’t worry" slogans, I present the technical facts, data flow, and exact code. What Maple Log can and cannot do is strictly determined by browser sandbox permissions and code.',
  updated: 'Updated',
  version: 'Build',
  scope: 'This report is provided by the author based on the live production build. All code is transparent and open to inspection.',
  summaryTitle: 'Key technical facts: What Maple Log cannot do',
  facts: [
    { label: 'Credentials', title: 'Never touches game passwords', detail: 'Runs purely in the browser without a login form. Never accesses or stores credentials.' },
    { label: 'Image processing', title: 'Frames recognized entirely locally', detail: 'Raw frames and OCR text are 100% processed in local memory, never sent to cloud or saved.' },
    { label: 'Client interaction', title: 'No client modification or automated play', detail: 'Never reads game memory, injects into processes, or sends key inputs to the game.' },
  ],
  contents: 'Table of contents',
  currentSection: 'Current section',
  workflowTitle: 'Architecture and data pipeline',
  workflowLead: 'Image recognition and statistics run entirely inside your browser sandbox, fully isolated from outside access.',
  workflow: [
    { title: 'Window authorization', detail: 'System dialog prompts you to choose the shared window' },
    { title: 'Frame capture', detail: 'Receives video frames only, with audio permanently disabled' },
    { title: 'Local offline OCR', detail: 'WASM model runs inside Web Worker to extract numbers' },
    { title: 'Local database', detail: 'EXP rates and history stay in browser IndexedDB' },
  ],
  localBoundary: 'Local execution environment: 100% contained within the browser sandbox',
  uploadBranch: 'Optional ranking sync: Qualifying stats (30+ mins) → Maple Log API → Leaderboards',
  uploadBranchNote: 'Only synchronizes public summary statistics. Never contains game frames and is decoupled from account security.',
  sections: [
    {
      id: 'account',
      nav: 'Credentials',
      title: 'Credentials and permission isolation',
      lead: 'Maple Log is a passive calculator based on pixels, completely separate from game accounts.',
      paragraphs: [
        'There is no game-login interface and no request for passwords, security questions, or two-factor codes. The only input is the video stream you explicitly grant.',
        '> **How it works: Why a webpage cannot record keyboard or mouse inputs**\n> 1. **Focus isolation**: Web JavaScript only receives events when its own tab element has active focus. When you type in the game, keystrokes are routed directly to the game process by the operating system.\n> 2. **Global hooks blocked by sandbox**: Capturing keystrokes across applications requires system hooks (like SetWindowsHookEx in Windows) or kernel drivers. The browser sandbox strictly prevents web scripts from doing this; only native executables (.exe) or malware can.\n> 3. **Pointer confinement**: As soon as the mouse exits the browser tab, the page completely loses all tracking of pointer movement and clicks.',
        'The getDisplayMedia API only delivers video frames. The browser sandbox strictly prevents it from reading process memory, obtaining session cookies, or intercepting system keys.',
      ],
      evidence: ['capture-request'],
    },
    {
      id: 'capture',
      nav: 'Capture permissions',
      title: 'Screen capture scope and revocation',
      lead: 'The webpage can only see the window you share. Audio capture is permanently turned off.',
      paragraphs: [
        'Screen capture can only be initiated through the operating system’s window selector. The webpage cannot bypass user consent, and audio capture is explicitly disabled in the constraints.',
        'Please note: ROI calibration only limits the text recognition sub-area, not the surface shared by the OS. If you share your entire desktop, the page can see desktop pixels. I strongly advise sharing only the game window.',
        'You can revoke access at any time using the browser’s "Stop sharing" button or by disconnecting in the app, which calls stop() on all media tracks immediately.',
      ],
      evidence: ['capture-options', 'capture-stop'],
    },
    {
      id: 'local',
      nav: 'Local OCR',
      title: 'Local offline recognition & storage',
      lead: 'PaddleOCR runs locally via WebAssembly inside a Web Worker. Data stays on your machine.',
      paragraphs: [
        '> **About PaddleOCR**: An open-source, ultra-lightweight OCR system from Baidu PaddlePaddle.',
        'Captured video frames are processed by a WebAssembly (WASM) engine inside a background Web Worker, using your own device’s CPU. No cloud OCR service is called, and raw frames are never transmitted.',
        'Model weights and static assets are downloaded from public CDNs on first use and cached locally. Every file is verified with SHA-256 hashes.',
        'Your training records, calibration coordinates, and settings are stored locally in IndexedDB (database: maple-log).',
      ],
      evidence: ['ocr-worker', 'ocr-predict', 'local-save', 'resource-integrity'],
    },
    {
      id: 'game',
      nav: 'Game interaction',
      title: 'Zero interaction with the game client',
      lead: 'Maple Log is a pure web tool. No injection, no memory reading, no simulated input.',
      paragraphs: [
        'Many players worry about ban risks. From a technical perspective: Maple Log installs no drivers, injects no DLLs, reads no game memory, and has no keyboard/mouse automation scripts. To the OS and anti-cheat, it is just a browser tab.',
        'Buttons such as "Start" and "Pause" only control internal timers and OCR scheduling within the page, with zero capability to send instructions to the game.',
        'These are the technical behaviors of the current code. Game operators determine how their rules apply; the correspondence is reproduced in section 01.',
      ],
      evidence: ['ocr-predict'],
    },
    {
      id: 'defense',
      nav: 'Server security',
      title: 'Architecture defense: Why server compromise cannot compromise your data',
      lead: 'Even under the hypothetical scenario of a server breach, your game data cannot be intercepted or stolen.',
      paragraphs: [
        'Players often ask: "Could the website be hacked to intercept my data? Are the URLs legitimate or phishing?" As an independent creator, I have architected Maple Log with a pure static frontend, zero centralized databases, and legally filed domains to eliminate these risks at their root.',
        'Hackers can only steal data that actually exists on a server. Maple Log servers store zero game frames or local records. The following four architectural pillars establish a comprehensive defense:',
      ],
      evidence: [],
    },
    {
      id: 'data',
      nav: 'Network data',
      title: 'Network data classification: What leaves your computer',
      lead: 'Raw game frames are never sent anywhere; only qualifying statistics sync to the public leaderboard.',
      paragraphs: [
        'When recording ends, if a session on the same map and level range lasts at least 30 minutes with positive EXP gain, a statistical summary will upload automatically to sync with the public MXDC (mxdc.dvg.cn) leaderboard for community reference.',
        'This feature is strictly for community benchmarks. Sync requests are filtered by an explicit allowlist and never package your local history or screen captures. The classification of data is listed below:',
      ],
      evidence: ['rank-fields', 'rank-submit', 'visitor-id'],
    },
    {
      id: 'verify',
      nav: 'Verify with F12',
      title: 'Three steps to verify in DevTools (F12)',
      lead: 'You do not need to take our word for it. Anyone can verify every claim using browser DevTools.',
      paragraphs: [
        '> **How it works: Web pages are inspectable by default**\n> A compiled installer or .exe is hard for a person to read. A webpage is the opposite: to run Maple Log, the browser must download the HTML, CSS and JavaScript it is executing onto your computer.\n> The code running now is already on this device. Press F12 and you can open it.',
        'The code snippets displayed below are extracted directly from the live build with verified file paths, line numbers, and SHA-256 hashes. You can check them directly:',
        'True transparency withstands inspection. By combining source inspection, network logs, and IndexedDB, you can confirm the entire pipeline.',
      ],
      evidence: [],
    },
  ] satisfies SecuritySection[],
  defenseCards: [
    {
      icon: 'globe' as const,
      tag: 'Official & Verified',
      title: 'Official Dual Domains & Rumor Debunking',
      desc: 'Maple Log operates only on two official genuine domains (fj.need.run and log.maplestory.pro), both sharing the exact same codebase. Rumors alleging that "fj.need.run is a phishing website" are completely false. Both domains have officially completed ICP filing in China.',
      link: { label: 'Verify on National MIIT ICP Filing System', url: 'https://beian.miit.gov.cn/' },
    },
    {
      icon: 'database' as const,
      tag: 'Stateless Frontend',
      title: 'Zero Sensitive Data at the Source',
      desc: 'Hackers cannot steal what does not exist. Maple Log employs a pure static stateless architecture with no login forms and no centralized user databases. Frames and records reside exclusively in your local browser IndexedDB; the server holds zero player data.',
      footnote: 'Defense line: Zero server storage; nothing for attackers to intercept',
    },
    {
      icon: 'layers' as const,
      tag: 'OS-Level Isolation',
      title: 'Browser Sandbox Physical Isolation',
      desc: 'Web code runs within the strict operating system sandbox of modern browsers. It is technically and physically impossible to read local files, intercept system keyboard events, inject DLLs, or access game memory from a webpage.',
      footnote: 'Physical boundary: Sandboxed by browser engine with zero game access',
    },
    {
      icon: 'fingerprint' as const,
      tag: 'Anti-Tampering',
      title: 'SHA-256 Asset Cryptographic Integrity',
      desc: 'All JavaScript bundles, Web Workers, and offline PaddleOCR models are bound to immutable SHA-256 digests. Any man-in-the-middle tampering or unauthorized code injection is automatically blocked by the browser prior to execution.',
      footnote: 'Integrity chain: All assets cryptographically hashed; injection fails fast',
    },
  ],
  dataHeaders: ['Data category', 'Content and handling', 'Destination & boundary'],
  dataRows: [
    ['Shared frames & captures', 'Processed in memory for digit extraction; no recordings saved', 'Local memory only, never uploaded'],
    ['History & settings', 'Session history, calibration ROIs, and user preferences', 'Local IndexedDB storage'],
    ['Qualifying rank stats', 'Map, level bucket, job, duration, and EXP delta for 30+ min sessions', 'Synced to MXDC leaderboard'],
    ['Anonymous identifier', 'Random visitor ID generated locally for ranking deduplication', 'Transmitted with qualifying rank summary'],
    ['Static assets & queries', 'OCR weights, UI icons, and public leaderboard queries', 'CDN and public static assets'],
  ],
  verifySteps: [
    {
      title: 'Sources · Inspect source code',
      text: 'Expand the code evidence cards below and copy the location info. Press F12 → Sources → Ctrl+P to find the file and line number. With Source Maps enabled, read original TypeScript code directly.',
    },
    {
      title: 'Network · Inspect traffic',
      text: 'Open DevTools Network tab with Preserve log checked. Use the app and end a session. Inspect the POST payload to /api/v1/rank/submissions to verify only JSON statistics are sent, with zero image uploads.',
    },
    {
      title: 'Application · Inspect local database',
      text: 'Go to Application → IndexedDB → maple-log to browse sessions, profiles, and settings. All historical data remains entirely under your control on your machine.',
    },
  ],
  officialResponse: {
    title: 'Official stance on bans for using Maple Log',
    nav: 'Official stance',
    intro: 'I asked official MapleStory streamer 无限跳票中 to consult the game’s detection team about using Maple Log. The conversation records are attached below.',
    conclusion: 'The team’s reply and the streamer’s relay did not prohibit use of Maple Log or state that using it would result in a ban.',
    records: {
      detection: {
        label: 'Detection team reply',
        translation: 'Literal translation: “Probably not, I think.”',
        recordedAt: 'Recorded: August 29, 21:09 (the image does not show a year)',
        imageAlt: 'Conversation with the detection team: a question about browser-based EXP statistics and the reply “Probably not, I think.”',
        imageCaption: 'Detection team conversation · Reply excerpt',
      },
      relay: {
        label: 'Official streamer’s relay',
        translation: 'Literal translation: “The MapleStory detection team said it should be fine.”',
        recordedAt: '',
        imageAlt: 'The official streamer relays: “The MapleStory detection team said it should be fine.”',
        imageCaption: 'Additional relay from the official streamer',
      },
    },
    privacyLabel: 'Privacy:',
    privacyNote: 'The respondent’s avatar is withheld for privacy. I guarantee that these conversation records are authentic and accurate.',
    streamerLabel: 'Official MapleStory streamer · Douyu',
    roomLabel: 'Room',
    sourceNote: 'The author supplied the streamer’s affiliation and account of the contact. Room details were checked on September 20, 2026.',
    enlarge: 'Enlarge conversation',
    enlargeAction: 'Enlarge',
    viewFullRecord: 'View full conversation',
    openOriginal: 'Open original image',
    previewTitle: 'Original conversation',
    imageError: 'The image could not load. Use “Open original image” to view it.',
    scopeLabel: 'Scope:',
    scope: 'Applies to the browser-based EXP statistics discussed in this inquiry, not to other tools, plugins or usage.',
    copyLink: 'Copy link · Official stance on bans for using Maple Log',
  },
  footerTitle: 'Report citation and feedback',
  footerText: 'You are welcome to reference or share this report. If you have technical questions or find discrepancies, please feel free to contact me directly.',
  legalAttestation: 'The technical explanations, source evidence, and related materials in this report are genuine and accurate. I personally bear legal responsibility for them.',
  contact: 'Contact author',
  share: 'Share report',
  copyLink: 'Copy link',
  copySummary: 'Copy summary',
  shareImage: 'Save share image',
  shareImageSaving: 'Generating share image…',
  shareImageSaved: 'Share image saved',
  shareImageFailed: 'Could not generate the share image. Please try again.',
  shareImageFile: 'maple-log-safety-report.png',
  shareCardKicker: 'No account theft · Local OCR · Independently verifiable',
  shareCardLead: 'A pure web tool: no memory reads, injection, or key simulation. Evidence is on the report page.',
  shareCardDomains: 'Official domains (ICP filed)',
  shareCardCta: 'Full report and source evidence',
  nativeShare: 'System share',
  print: 'Print / Save PDF',
  shareSummary: 'Maple Log Safety & Privacy Report: No password access, local offline frame recognition, no memory injection or automated keystrokes. Technical evidence & DevTools verification:',
  copied: 'Copied to clipboard',
  copyFailed: 'Copy failed. Please manually select and copy below.',
  shareFailed: 'System share unavailable. Link copied.',
  manualCopy: 'Text to copy manually',
  evidence: 'View code evidence',
  source: 'Source location',
  codeCopy: 'Copy snippet',
  openResource: 'Open build file in new tab',
  locate: 'Copy F12 navigation hint',
  loadingEvidence: 'Loading code evidence for this build…',
  evidenceError: 'Temporarily unable to load code evidence. The text remains readable. Please retry.',
  retry: 'Retry loading evidence',
  evidenceScope: 'Code is extracted from current production build. SHA-256 for integrity verification.',
  digest: 'Evidence digest SHA-256',
  evidenceLabels: {
    'capture-request': 'Display capture authorization entry',
    'capture-options': 'Capture options & audio disabled',
    'capture-stop': 'Stop and release media tracks',
    'ocr-worker': 'Worker & local WASM configuration',
    'ocr-predict': 'Local OCR inference invocation',
    'local-save': 'Local persistence of session history',
    'resource-integrity': 'SHA-256 asset integrity check',
    'rank-fields': 'Allowlist for submitted leaderboard fields',
    'rank-submit': 'Submission endpoint & request payload',
    'visitor-id': 'Random visitor ID generation',
  } as Record<string, string>,
}

export const securityCopy = { 'zh-CN': zh, en }
